import type { PermissionCatalog } from "../Auth/PermissionsContext";

/**
 * PÉRIMÈTRE DE CONTENU d'un rôle, résumé en quelques étiquettes lisibles.
 *
 * Problème résolu : la carte d'un rôle n'affichait que son nom, et il fallait ouvrir l'éditeur puis
 * déplier chaque groupe pour savoir sur QUOI il donne la main. Les étiquettes répondent d'un coup
 * d'oeil, en remontant au niveau le plus général qui reste exact :
 *
 * 1. le rôle touche TOUT le contenu (joker `*`, `content:*:…`, ou tous les types du catalogue) :
 *    une seule étiquette « Tout le contenu ». On ne liste jamais les 67 types d'un administrateur ;
 * 2. il couvre un GROUPE entier (tous les types de « Statistiques ») : une étiquette pour le
 *    groupe, et ses types ne sont pas répétés ;
 * 3. il couvre une PARTIE d'un groupe : une étiquette « Groupe (n/m) », qui dit d'un coup le
 *    rayon et son étendue au lieu d'aligner n titres de types ;
 * 4. il ne touche qu'un seul type d'un groupe : le titre de ce type, plus parlant qu'un « 1/16 » ;
 * 5. il ne touche aucun contenu (rôle purement fonctionnel) : « Aucun contenu ».
 *
 * Au-delà de {@link MAX_ETIQUETTES}, le reste est replié dans une étiquette « +N autres » qui porte
 * la liste complète en infobulle : une carte reste lisible, sans jamais mentir par omission.
 * Les actions (lire / écrire / publier / mettre en production) ne sont PAS distinguées ici : la
 * question posée est « quel contenu », l'éditeur reste la source de détail.
 */
export const MAX_ETIQUETTES = 6;

export type ScopeTone = "all" | "group" | "type" | "more" | "none";

export interface ScopeTag {
	/** Texte affiché dans la pastille. */
	label: string;
	/** Infobulle (title=) : précise ce que l'étiquette recouvre. */
	hint: string;
	tone: ScopeTone;
}

/** Les actions de contenu de la grammaire de jetons (`content:<sélecteur>:<action>`). */
const ACTIONS_CONTENU = new Set(["read", "write", "publish", "promote"]);

/** Découpe un jeton `content:<sélecteur>:<action>` ; rend null pour tout le reste. */
const selecteurDeContenu = (jeton: string): string | null => {
	const parts = (jeton ?? "").trim().split(":");
	if (parts.length !== 3) return null;
	if (parts[0] !== "content") return null;
	if (!ACTIONS_CONTENU.has(parts[2])) return null;
	const sel = parts[1].trim();
	return sel.length > 0 ? sel : null;
};

/**
 * Clés de types de contenu réellement touchées par les grants, jokers et groupes résolus.
 * `null` = le rôle touche TOUT (joker), ce que l'appelant traite à part : un joker reste vrai même
 * si le catalogue s'enrichit plus tard, alors qu'une énumération, elle, serait périmée.
 */
export const typesTouchesParLeRole = (
	permissions: ReadonlyArray<string> | null | undefined,
	catalog: PermissionCatalog | null | undefined
): Set<string> | null => {
	const jetons = (permissions ?? []).map((p) => (p ?? "").trim()).filter(Boolean);
	if (jetons.includes("*")) return null;

	const groupes = catalog?.groups ?? [];
	const types = new Set<string>();
	for (const jeton of jetons) {
		const sel = selecteurDeContenu(jeton);
		if (!sel) continue;
		if (sel === "*") return null;
		if (sel.startsWith("@")) {
			const groupe = groupes.find((g) => g.key === sel.slice(1));
			for (const tk of groupe?.typeKeys ?? []) types.add(tk);
			continue;
		}
		types.add(sel);
	}
	return types;
};

/** Étiquettes de périmètre d'un rôle, dans l'ordre : groupes complets d'abord, puis types isolés. */
export const describeRoleContentScope = (
	permissions: ReadonlyArray<string> | null | undefined,
	catalog: PermissionCatalog | null | undefined
): ScopeTag[] => {
	const touches = typesTouchesParLeRole(permissions, catalog);
	const groupes = catalog?.groups ?? [];
	const typesDuCatalogue = catalog?.contentTypes ?? [];
	const tout: ScopeTag = { label: "Tout le contenu", hint: "Tous les types de contenu du catalogue.", tone: "all" };

	if (touches === null) return [tout];

	// Catalogue pas encore chargé : on n'affiche RIEN plutôt que d'affirmer « aucun contenu », qui
	// serait faux le temps d'un aller-retour réseau.
	const catalogueVide = (catalog?.groups?.length ?? 0) === 0 && (catalog?.contentTypes?.length ?? 0) === 0;
	if (catalogueVide) return [];

	if (touches.size === 0) {
		return [{ label: "Aucun contenu", hint: "Ce rôle ne donne accès à aucun type de contenu.", tone: "none" }];
	}

	// Couverture totale ÉNUMÉRÉE (sans joker) : même conclusion, même étiquette unique.
	const connus = typesDuCatalogue.map((t) => t.key);
	if (connus.length > 0 && connus.every((k) => touches.has(k))) return [tout];

	const restants = new Set(touches);
	const etiquettes: ScopeTag[] = [];
	const libelle = (cle: string) => typesDuCatalogue.find((t) => t.key === cle)?.label ?? cle;

	// Un groupe à la fois, dans l'ordre du catalogue : entier, partiel, ou un seul type.
	for (const g of groupes) {
		const cles = g.typeKeys ?? [];
		if (cles.length === 0) continue;
		const touchees = cles.filter((k) => restants.has(k));
		if (touchees.length === 0) continue;
		for (const k of touchees) restants.delete(k);

		if (touchees.length === cles.length) {
			etiquettes.push({
				label: g.label,
				hint: `${cles.length} type(s) de contenu : ${cles.join(", ")}`,
				tone: "group"
			});
		} else if (touchees.length === 1) {
			// Un seul type : son titre dit plus qu'un « 1/16 ».
			etiquettes.push({ label: libelle(touchees[0]), hint: `Type de contenu : ${touchees[0]}`, tone: "type" });
		} else {
			etiquettes.push({
				label: `${g.label} (${touchees.length}/${cles.length})`,
				hint: `${touchees.length} type(s) sur ${cles.length} : ${touchees.join(", ")}`,
				tone: "group"
			});
		}
	}

	// Ce qui reste ne correspond à aucun type du catalogue : des grants HÉRITÉS, laissés par un
	// type supprimé ou renommé. Ils ne donnent accès à rien, donc une seule étiquette discrète les
	// signale (la liste exacte reste en infobulle) plutôt que d'encombrer la carte.
	const orphelins = [...restants].sort((a, b) => a.localeCompare(b, "fr", { sensitivity: "base" }));
	if (orphelins.length > 0) {
		etiquettes.push({
			label: `${orphelins.length} type${orphelins.length > 1 ? "s" : ""} hors catalogue`,
			hint: `Clés sans type correspondant (droits hérités, sans effet) : ${orphelins.join(", ")}`,
			tone: "more"
		});
	}

	if (etiquettes.length <= MAX_ETIQUETTES) return etiquettes;

	const gardees = etiquettes.slice(0, MAX_ETIQUETTES - 1);
	const repliees = etiquettes.slice(MAX_ETIQUETTES - 1);
	gardees.push({
		label: `+${repliees.length} autres`,
		hint: repliees.map((e) => e.label).join(", "),
		tone: "more"
	});
	return gardees;
};

/** Classes Tailwind de la pastille, par nature d'étiquette (palette réelle de l'admin). */
export const SCOPE_TAG_CLASSES: Record<ScopeTone, string> = {
	all: "border-indigo-200 bg-indigo-50 text-indigo-700",
	group: "border-sky-200 bg-sky-50 text-sky-700",
	type: "border-slate-200 bg-slate-100 text-slate-600",
	more: "border-slate-200 bg-white text-slate-500",
	none: "border-slate-200 bg-slate-50 text-slate-400"
};
